/*
=========================================================
* Promo Popup (one-time, desktop only)
=========================================================
*
* Dismissible dialog advertising the active limited-time promo, shown once
* per promo (localStorage key includes promo name + end date). Desktop only:
* Google penalizes intrusive interstitials on mobile landing pages, and the
* PromoBanner already covers mobile. Renders nothing outside the promo
* window (constants/promo.js), so it self-expires without a redeploy.
*
* The header graphic is an inline SVG (rising summer sun + "ringing" ripple
* arcs — a nod to the phone agent), so no external asset is loaded.
*/

import { useEffect, useState } from "react";

import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import useMediaQuery from "@mui/material/useMediaQuery";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";

import useSignupUrl from "hooks/useSignupUrl";
import { isPromoActive, PROMO_TRIAL } from "constants/promo";

const DISMISS_KEY = `promo-popup-dismissed:${PROMO_TRIAL.name}:${PROMO_TRIAL.signupEnd}`;
const OPEN_DELAY_MS = 1200;

function wasDismissed() {
  try {
    return typeof window !== "undefined" && window.localStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    return false;
  }
}

function markDismissed() {
  try {
    window.localStorage.setItem(DISMISS_KEY, "1");
  } catch {
    // localStorage unavailable (private browsing) — popup reappears next visit
  }
}

/** Decorative header: gold sun rising over the horizon, thin rays, ring ripples. */
function SunriseGraphic() {
  return (
    <svg
      viewBox="0 0 480 190"
      width="100%"
      height="100%"
      preserveAspectRatio="xMidYMax slice"
      role="img"
      aria-label={`${PROMO_TRIAL.name} promotion`}
    >
      <defs>
        <linearGradient id="promoSky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#14130F" />
          <stop offset="100%" stopColor="#2B2416" />
        </linearGradient>
        <linearGradient id="promoSun" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FFE3A3" />
          <stop offset="100%" stopColor="#F5B94E" />
        </linearGradient>
        <radialGradient id="promoGlow" cx="50%" cy="100%" r="75%">
          <stop offset="0%" stopColor="#FFD37E" stopOpacity="0.35" />
          <stop offset="60%" stopColor="#FFD37E" stopOpacity="0.08" />
          <stop offset="100%" stopColor="#FFD37E" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* sky */}
      <rect x="0" y="0" width="480" height="190" fill="url(#promoSky)" />
      <rect x="0" y="0" width="480" height="190" fill="url(#promoGlow)" />

      {/* thin sun rays */}
      <g stroke="#FFD37E" strokeWidth="2" strokeLinecap="round" opacity="0.55">
        <line x1="240" y1="190" x2="240" y2="34" transform="rotate(-72 240 190)" opacity="0.35" />
        <line x1="240" y1="190" x2="240" y2="52" transform="rotate(-54 240 190)" />
        <line x1="240" y1="190" x2="240" y2="30" transform="rotate(-36 240 190)" opacity="0.4" />
        <line x1="240" y1="190" x2="240" y2="48" transform="rotate(-18 240 190)" />
        <line x1="240" y1="190" x2="240" y2="26" />
        <line x1="240" y1="190" x2="240" y2="48" transform="rotate(18 240 190)" />
        <line x1="240" y1="190" x2="240" y2="30" transform="rotate(36 240 190)" opacity="0.4" />
        <line x1="240" y1="190" x2="240" y2="52" transform="rotate(54 240 190)" />
        <line x1="240" y1="190" x2="240" y2="34" transform="rotate(72 240 190)" opacity="0.35" />
      </g>

      {/* ringing ripples above the sun — the phone is ringing */}
      <g fill="none" stroke="#F4F1EA" strokeLinecap="round">
        <path d="M 175 132 A 65 65 0 0 1 305 132" strokeWidth="2.5" opacity="0.85" />
        <path d="M 152 138 A 88 88 0 0 1 328 138" strokeWidth="2" opacity="0.45" />
        <path d="M 129 144 A 111 111 0 0 1 351 144" strokeWidth="1.5" opacity="0.22" />
      </g>

      {/* rising sun */}
      <path d="M 196 190 A 44 44 0 0 1 284 190 Z" fill="url(#promoSun)" />

      {/* sparkles */}
      <g fill="#FFE3A3">
        <circle cx="120" cy="60" r="2.4" opacity="0.9" />
        <circle cx="366" cy="48" r="1.8" opacity="0.7" />
        <circle cx="404" cy="96" r="2.2" opacity="0.5" />
        <circle cx="84" cy="110" r="1.6" opacity="0.5" />
        <circle cx="318" cy="24" r="1.4" opacity="0.6" />
      </g>

      {/* horizon */}
      <line x1="0" y1="189" x2="480" y2="189" stroke="#FFD37E" strokeWidth="2" opacity="0.9" />
    </svg>
  );
}

function PromoPopup() {
  const signupUrl = useSignupUrl();
  const [open, setOpen] = useState(false);
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up("md"));

  useEffect(() => {
    if (!isPromoActive() || !isDesktop || wasDismissed()) return undefined;
    const timer = setTimeout(() => setOpen(true), OPEN_DELAY_MS);
    return () => clearTimeout(timer);
  }, [isDesktop]);

  if (!isPromoActive()) return null;

  const months = Math.round(PROMO_TRIAL.trialDays / 30);
  const endsLabel = new Date(`${PROMO_TRIAL.signupEnd}T00:00:00`).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
  });

  const close = () => {
    setOpen(false);
    markDismissed();
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
    >
      {/* Header graphic */}
      <MKBox sx={{ height: 190, background: "#14130F", position: "relative" }}>
        <SunriseGraphic />
        <IconButton
          aria-label="Close promotion"
          onClick={close}
          size="small"
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            color: "rgba(255,255,255,0.8)",
            background: "rgba(0,0,0,0.25)",
            "&:hover": { background: "rgba(0,0,0,0.45)" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </MKBox>

      {/* Body */}
      <MKBox px={4} pt={3} pb={4} textAlign="center" sx={{ background: "#fff" }}>
        <MKTypography
          component="p"
          sx={{
            color: "#B98A2E",
            fontWeight: 700,
            fontSize: "0.8rem",
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            mb: 1,
          }}
        >
          ☀️ {PROMO_TRIAL.name} · Limited Time
        </MKTypography>
        <MKTypography
          component="h2"
          sx={{ color: "#14130F", fontWeight: 800, fontSize: "2.15rem", lineHeight: 1.15, mb: 1 }}
        >
          {months} Months Free
        </MKTypography>
        <MKTypography
          component="p"
          sx={{ color: "rgba(20,19,15,0.65)", fontSize: "0.95rem", lineHeight: 1.55, mb: 2.5 }}
        >
          Every plan. Full features. Cancel anytime.
          <br />
          Offer ends {endsLabel}.
        </MKTypography>
        <MKButton
          component="a"
          href={signupUrl}
          onClick={markDismissed}
          fullWidth
          size="large"
          sx={{
            background: "#14130F",
            color: "#FFD37E",
            fontWeight: 700,
            "&:hover": { background: "#2B2416", color: "#fff" },
            "&:focus:not(:hover)": { background: "#14130F", color: "#FFD37E" },
          }}
        >
          Start Free Trial →
        </MKButton>
        <MKTypography
          component="p"
          sx={{ color: "rgba(20,19,15,0.45)", fontSize: "0.75rem", mt: 1.5 }}
        >
          No charge until your trial ends. No contract.
        </MKTypography>
      </MKBox>
    </Dialog>
  );
}

export default PromoPopup;
